import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "acx_cart_v1";

// Cart is persisted locally in the browser. At checkout we hand the line items
// to Shopify (cartCreate) and redirect to Shopify's hosted checkout, which owns
// the order, payments, shipping and taxes.
export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      return s ? JSON.parse(s) || [] : [];
    } catch (e) {
      console.warn("Cart: failed to read saved cart from localStorage", e);
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn("Cart: failed to persist cart to localStorage", e);
    }
  }, [items]);

  const addItem = useCallback((product, variant, qty = 1) => {
    const v = variant || (product.variants && product.variants[0]) || { label: "Default", price: product.price };
    const key = `${product.slug}__${v.label}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          image: product.images?.[0],
          variant: v.label,
          price: v.price,
          qty,
        },
      ];
    });
    toast.success("Added to cart", { description: `${product.name} · ${v.label}` });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  // Add many lines at once (used by one-tap reorder)
  const addMany = useCallback((lines) => {
    setItems((prev) => {
      const map = new Map(prev.map((i) => [i.key, { ...i }]));
      for (const l of lines) {
        const key = `${l.slug}__${l.variant}`;
        if (map.has(key)) map.get(key).qty += l.qty || 1;
        else map.set(key, { key, slug: l.slug, name: l.name, image: l.image, variant: l.variant, price: l.price, qty: l.qty || 1 });
      }
      return Array.from(map.values());
    });
    toast.success("Items added to cart");
    setOpen(true);
  }, []);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);

  const value = useMemo(
    () => ({ items, count, subtotal, addItem, addMany, removeItem, updateQty, clear, open, setOpen }),
    [items, count, subtotal, addItem, addMany, removeItem, updateQty, clear, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
